import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tab, Tabs, Form, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';

// Shared chart components
import StatsHourlyChart from '../../../statistics/charts/StatsHourlyChart';
import StatsDailyChart from '../../../statistics/charts/StatsDailyChart';
import StatsPerformanceCards from '../../../statistics/charts/StatsPerformanceCards';
import StatsCurrentRate from '../../../statistics/charts/StatsCurrentRate';
import StatsStatusBreakdown from '../../../statistics/charts/StatsStatusBreakdown';
import StatsAnomaliesDisplay from '../../../statistics/charts/StatsAnomaliesDisplay';

// Actions
import {
	get_worker_stats_hourly,
	get_worker_stats_days,
	get_worker_stats_current,
	get_worker_stats_performance,
	get_worker_stats_type,
	get_worker_stats_anomalies
} from '../../../../actions/workerStatsActions';

const WorkerStatsOverview = ({ instanceId }) => {
	const dispatch = useDispatch();

	const {
		worker_stats_loading,
		worker_stats_hourly,
		worker_stats_days,
		worker_stats_current,
		worker_stats_performance,
		worker_stats_type,
		worker_stats_anomalies
	} = useSelector(state => state.workerStats);

	const [hours, setHours] = useState(24);
	const [days, setDays] = useState(7);
	const [jobType, setJobType] = useState('');
	const [typeHours, setTypeHours] = useState(24);

	useEffect(() => {
		dispatch(get_worker_stats_performance(instanceId));
		dispatch(get_worker_stats_current(instanceId));
	}, []);

	const handleHourlyChange = (selectedHours) => {
		setHours(selectedHours);
		dispatch(get_worker_stats_hourly(instanceId, selectedHours));
	};

	const handleDailyChange = (selectedDays) => {
		setDays(selectedDays);
		dispatch(get_worker_stats_days(instanceId, selectedDays));
	};

	const handleTypeChange = (type) => {
		setJobType(type);
		if (type) {
			dispatch(get_worker_stats_type(instanceId, type, typeHours));
		}
	};

	return (
		<Tabs
			defaultActiveKey='overview'
			className='mt-3'
			justify
		>
			<Tab eventKey='overview' title='Overview'>
				<div className='mt-3'>
					<StatsCurrentRate rate={worker_stats_performance?.data || worker_stats_current?.data} />
					<StatsPerformanceCards performance={worker_stats_performance?.data} />
					<StatsStatusBreakdown breakdown={worker_stats_performance?.data?.status_breakdown} />
				</div>
			</Tab>

			<Tab
				eventKey='hourly'
				title='Hourly'
				onEnter={() => { if (!worker_stats_hourly?.data?.length) dispatch(get_worker_stats_hourly(instanceId, hours)); }}
			>
				<div className='mt-3'>
					<Row className='mb-3 align-items-center'>
						<Col xs={4}>
							<Form.Select value={hours} onChange={(e) => handleHourlyChange(Number(e.target.value))}>
								<option value={12}>Last 12 hours</option>
								<option value={24}>Last 24 hours</option>
								<option value={48}>Last 48 hours</option>
							</Form.Select>
						</Col>
					</Row>
					<StatsHourlyChart data={worker_stats_hourly?.data} loading={worker_stats_loading} />
				</div>
			</Tab>

			<Tab
				eventKey='daily'
				title='Daily'
				onEnter={() => { if (!worker_stats_days?.data?.length) dispatch(get_worker_stats_days(instanceId, days)); }}
			>
				<div className='mt-3'>
					<Row className='mb-3 align-items-center'>
						<Col xs={4}>
							<Form.Select value={days} onChange={(e) => handleDailyChange(Number(e.target.value))}>
								<option value={7}>Last 7 days</option>
								<option value={14}>Last 14 days</option>
								<option value={30}>Last 30 days</option>
							</Form.Select>
						</Col>
					</Row>
					<StatsDailyChart data={worker_stats_days?.data} loading={worker_stats_loading} />
				</div>
			</Tab>

			<Tab eventKey='type' title='By Type'>
				<div className='mt-3'>
					<Row className='mb-3 align-items-center'>
						<Col xs={4}>
							<Form.Control
								placeholder='Job type (e.g. Payload, Dynamic)'
								value={jobType}
								onChange={(e) => setJobType(e.target.value)}
								onKeyDown={(e) => { if (e.key === 'Enter') handleTypeChange(jobType); }}
							/>
						</Col>
						<Col xs={3}>
							<Form.Select value={typeHours} onChange={(e) => setTypeHours(Number(e.target.value))}>
								<option value={12}>12 hours</option>
								<option value={24}>24 hours</option>
								<option value={48}>48 hours</option>
							</Form.Select>
						</Col>
						<Col xs={2}>
							<button className='btn submit-btn' onClick={() => handleTypeChange(jobType)} disabled={!jobType}>
								<i className='bi bi-search me-1'></i> Search
							</button>
						</Col>
					</Row>
					{worker_stats_type?.data?.by_hour &&
						<StatsHourlyChart data={worker_stats_type.data.by_hour} loading={worker_stats_loading} />
					}
				</div>
			</Tab>

			<Tab
				eventKey='anomalies'
				title='Anomalies'
				onEnter={() => { if (!worker_stats_anomalies?.data?.anomalies) dispatch(get_worker_stats_anomalies(instanceId)); }}
			>
				<div className='mt-3'>
					<StatsAnomaliesDisplay
						anomalies={worker_stats_anomalies?.data?.anomalies}
						summary={worker_stats_anomalies?.data?.summary}
					/>
				</div>
			</Tab>
		</Tabs>
	);
}

WorkerStatsOverview.propTypes = {
	instanceId: PropTypes.string.isRequired
}

export default WorkerStatsOverview;
